import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { MaterialModule } from '../material.module';
import { DisplayValue } from '@newgenus/common';
import fuzzysort from 'fuzzysort';

@Component({
    selector: 'shared-searchable-select',
    templateUrl: './searchable-select.component.html',
    imports: [MaterialModule, FormsModule, ReactiveFormsModule, NgClass, NgFor, NgIf],
    styles: [
        '.additional-selection { opacity: 0.75; font-size: 0.75em; }',
        '.hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }',
        '.hide-scrollbar::-webkit-scrollbar { display: none; }',
        // Compact mode — piercing CSS lives in the same component as the
        // mat-form-field it targets so ::ng-deep is scoped correctly.
        ':host ::ng-deep mat-form-field.searchable-select--compact .mat-mdc-form-field-subscript-wrapper { display: none; }',
        ':host ::ng-deep mat-form-field.searchable-select--compact .mat-mdc-text-field-wrapper { padding-top: 0; padding-bottom: 0; }',
    ],
})
export class SearchableSelectComponent implements OnInit, OnChanges {
    /**
     * The appearance of the component. This can be either 'fill' or 'outline', according to the Material Design spec.
     *
     * @type {('fill' | 'outline')}
     */
    @Input() public appearance: 'fill' | 'outline' = 'fill';

    /**
     * The data being injected into this component. A copy of this data is used for the filtered data, and that is what is displayed.
     */
    @Input() public data: DisplayValue[] = [];

    /**
     * Whether or not the component is disabled - if you are using a form control, you should use the formControl.disable() instead.
     */
    @Input() public disabled = false;

    /**
     * The form control that is used to control the value of the searchable select component.
     *
     * @optional If not provided, a new form control will be created.
     */
    @Input() public control = new FormControl();

    /**
     * The label of the searchable select component.
     *
     * @optional If not provided, the label will be 'Select an option'.
     */
    @Input() public label = 'Select an option';

    /**
     * Whether or not the searchable select component is multiple.
     *
     * @optional If not provided, the searchable select component will not be multiple.
     */
    @Input() public multiple: boolean | null = false;

    /**
     * The placeholder of the searchable select component.
     *
     * @optional If not provided, the placeholder will be 'Enter a search term'.
     */
    @Input() public placeholder = 'Enter a search term';

    /**
     * Whether or not the searchable select component is required - if you are using a form control, you should declare that formControl as required instead.
     *
     * @optional If not provided, the searchable select component will not be required.
     * @example ```typescript
     * // In your component:
     * public form = new FormGroup({
     *  searchableSelect: new FormControl('', [Validators.required]),
     * });
     * ```
     */
    @Input() public required = false;

    /**
     * When selecting multiple values, whether or not to display the selected values (true) or displays (false).
     */
    @Input() public displayValue = true;

    /**
     * Event emitter that emits the selected value(s) when the value(s) change.
     *
     * @type {(EventEmitter<DisplayValue | DisplayValue[]>)}
     */
    @Output() public selected: EventEmitter<any> = new EventEmitter();

    @Input() public value: any = [];
    @Output() public valueChange: EventEmitter<any> = new EventEmitter();

    /**
     * The classes that are applied to the searchable select component.
     *
     * @type {string[]}
     * @memberof SearchableSelectComponent
     */
    @Input()
    public classes: string[] = [];

    /**
     * Optional Material icon name to show before each option.
     */
    @Input() public icon: string | null = null;

    /**
     * Optional CSS color for the icon.
     */
    @Input() public iconColor: string | null = null;

    /**
     * Width of the dropdown panel. Accepts any value mat-select's `panelWidth` accepts
     * (number of pixels, CSS length string, 'auto', or null to match the trigger).
     */
    @Input() public panelWidth: string | number | null = 'auto';

    /**
     * Controls when the floating label is shown. Default 'auto' matches Material's
     * native behaviour (label sits inside the trigger until a value is chosen, then
     * floats up). Set 'always' to keep the label docked above the trigger at all
     * times — useful when several pickers sit on the same row and you need a
     * constant form-field height regardless of selection state.
     */
    @Input() public floatLabel: 'auto' | 'always' = 'auto';

    /**
     * Compact mode — hides the form-field subscript wrapper (the hint/error band
     * below the input) and removes the text-field vertical padding so the picker
     * matches the height of plain <input> form-fields with no hints. Use this
     * when embedding the picker in a dense table row alongside other inputs that
     * also use the compact treatment.
     */
    @Input() public compact = false;

    /**
     * The search term that is used to filter the data - this variable is only used if the data changes while the component is active with a search.
     */
    public searchTerm = '';

    /**
     * The filtered data that is displayed in the searchable select component.
     */
    public filteredData: DisplayValue[] = [];

    public ngOnInit(): void {
        // Set the filtered data to the data - must be done in ngOnInit() for the @inputs to have been set.
        this.updateFilteredData();

        if (this.value && this.value.length > 0) {
            setTimeout(() => {
                this.control.setValue(this.value);
                this.updateFilteredData(); // Update ordering after setting value
            });
        }

        // Subscribe to control value changes to automatically update filtered data ordering
        this.control.valueChanges.subscribe(() => {
            this.updateFilteredData();
        });
    }

    public onSelectChange(event: MatSelectChange) {
        // Emit the selected value(s).
        this.selected.emit(event.value);
        this.value = event.value;
        this.valueChange.emit(event.value);

        this.control.setValue(event.value);

        // Update filtered data to reflect new selection ordering
        this.updateFilteredData();
    }

    private updateFilteredData(): void {
        // For multi-select, show selected items at the top
        if (this.multiple && this.control.value && Array.isArray(this.control.value)) {
            const selectedValues = this.control.value;
            const selectedItems = this.data.filter((item) => selectedValues.includes(item.value));
            const unselectedItems = this.data.filter((item) => !selectedValues.includes(item.value));
            this.filteredData = [...selectedItems, ...unselectedItems];
        } else {
            // Single select: normal order
            this.filteredData = this.data.slice();
        }
    }

    public onSearchChange(term: string): void {
        // For multi-select, always show selected items at the top
        if (this.multiple && this.control.value && Array.isArray(this.control.value)) {
            const selectedValues = this.control.value;
            const selectedItems = this.data.filter((item) => selectedValues.includes(item.value));

            if (!term) {
                // No search term: show selected items first, then unselected items
                const unselectedItems = this.data.filter((item) => !selectedValues.includes(item.value));
                this.filteredData = [...selectedItems, ...unselectedItems];
                return;
            }

            // With search term: filter using fuzzysort, but keep selected items at top
            const searchResults = fuzzysort.go(term, this.data || [], { key: 'display' }).map((c) => c.obj);

            // Combine selected items with search results, removing duplicates
            const allItems = [...selectedItems, ...searchResults];
            this.filteredData = allItems.filter(
                (item, index, arr) => arr.findIndex((i) => i.value === item.value) === index
            );
        } else {
            // Single select or no selection: normal behavior
            if (!term) {
                this.filteredData = this.data?.slice() || [];
                return;
            }

            const searchResults = fuzzysort.go(term, this.data || [], { key: 'display' }).map((c) => c.obj);
            this.filteredData = searchResults;
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        // If the data changes, update the filtered data.
        if (changes['data'] && changes['data'].isFirstChange() === false) {
            // Update the filtered data with proper ordering.
            this.updateFilteredData();

            // Apply search filter if there is one.
            if (this.searchTerm && this.searchTerm.length > 0)
                setTimeout(() => {
                    this.onSearchChange(this.searchTerm);
                });
        }

        if (changes['disabled']) {
            changes['disabled'].currentValue === true ? this.control.disable() : this.control.enable();
        }

        // When the user changes multiple from true/false or false/true, the value of the control must be set to an array or a string.
        // When multiple is true, the value must be an array, and when multiple is false and the control has a value, then the first entry in the array is set as the value.
        if (changes['multiple'] && changes['multiple'].isFirstChange() === false) {
            const value = this.control.value;
            this.control.setValue(null);

            // Wrapped in setTimeouts to allow the UI to update.
            if (this.multiple) {
                setTimeout(() => {
                    if (Array.isArray(value)) this.control.setValue(value ? [value] : []);
                    else this.control.setValue(value ? [value] : []);
                });
            } else {
                setTimeout(() => {
                    if (Array.isArray(value)) this.control.setValue(value ? value[0] : '');
                    else this.control.setValue(value ? value : '');
                });
            }

            this.control.updateValueAndValidity();
        }
    }

    /**
     * If the displayValue is true, then the value of the selected option(s) is displayed.
     * If the displayValue is false, then the display of the selected options(s) is displayed.
     *
     * @readonly
     */
    public get displayValueText(): string {
        if (this.multiple) {
            const values = this.control.value || [];
            if (this.displayValue) {
                if (values.length < 2) return this.control.value !== null ? this.control.value[0] : '';
                else
                    return `${this.control.value[0]} (+${values.length - 1} ${values.length === 2 ? 'other' : 'others'})`;
            } else {
                if (values.length < 2) return this.data.find((d) => d.value === values[0])?.display || '';
                else
                    return `${this.data.find((d) => d.value === values[0])?.display} (+${values.length - 1} ${values.length === 2 ? 'other' : 'others'})`;
            }
        } else {
            if (this.displayValue) return this.control.value || '';
            else return this.data.find((d) => d.value === this.control.value)?.display || '';
        }
    }
}
